import React from 'react';
import { useSelector } from 'react-redux';
import { ServiceIcon } from './IconComponents';
import { FaServer, FaGlobe, FaCog, FaArrowRight } from 'react-icons/fa';
import LoadingOverlay from './LoadingOverlay';

const ServiceSelection = ({ onServiceSelect }) => {
  const { serviceStatus, statusLoading, statusError } = useSelector(state => state.services);

  const services = [
    {
      id: 'cloudVPS',
      name: 'Cloud VPS',
      description: 'Serveur virtuel privé dans le cloud avec OS Linux au choix',
      features: ['2-16 vCPU', '4-32GB RAM', '40-320GB SSD', 'OS Linux variés']
    },
    {
      id: 'orchestrator',
      name: 'Orchestrateur',
      description: 'Services d\'orchestration de conteneurs (K8S, Docker, Podman)',
      features: ['Kubernetes Mono', 'Docker Engine', 'Podman', 'Configurations flexibles']
    },
    {
      id: 'dbaas',
      name: 'DBaaS',
      description: 'Base de données en tant que service',
      features: ['MySQL', 'PostgreSQL', 'Redis', 'MongoDB']
    },
    {
      id: 'domains',
      name: 'Domaines',
      description: 'Enregistrement et gestion de noms de domaine',
      features: ['40+ extensions', 'Vérification disponibilité', 'Gestion DNS', 'Renouvellement']
    },
    {
      id: 'cmsaas',
      name: 'CMSaaS',
      description: 'CMS en tant que service - Déploiement automatique',
      features: ['WordPress', 'Joomla', 'Drupal', 'SSL inclus', 'Mises à jour auto']
    },
    {
      id: 'dedicatedServer',
      name: 'Serveur Dédié',
      description: 'Serveurs dédiés haute performance',
      features: ['Linux & Windows', '3-24 CPU', '24-128GB RAM', '180GB-2TB SSD']
    }
  ];

  return (
    <>
      {/* Overlay de chargement au premier chargement */}
      <LoadingOverlay 
        isLoading={statusLoading} 
        message="Chargement des services disponibles..."
      />

      <div className="relative space-y-12">
        {/* Hero Section simple et élégant */}
      <div className="text-center py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Services Cloud Professionnels
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
          Choisissez le service qui correspond à vos besoins. Configuration simple, déploiement rapide, support technique inclus.
        </p>
        
        {/* Indicateur de status discret */}
        <div className="inline-flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span className="text-green-700 text-sm font-medium">Tous les services opérationnels</span>
        </div>
      </div>

      {/* Message d'erreur si l'API n'est pas disponible */}
      {statusError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Impossible de charger les statuts des services</h3>
              <p className="mt-1 text-sm text-red-700">
                Tous les services sont temporairement indisponibles. Veuillez réessayer dans quelques instants.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Services Grid avec design doux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => {
          // Si pas de statut disponible (objet vide ou erreur API), considérer comme indisponible
          const hasStatus = serviceStatus[service.id] !== undefined;
          const isEnabled = hasStatus && serviceStatus[service.id]?.enabled && !serviceStatus[service.id]?.maintenance;
          const isInMaintenance = hasStatus && serviceStatus[service.id]?.maintenance;

          return (
            <div
              key={service.id}
              onClick={() => isEnabled && onServiceSelect(service.id)}
              className={`service-card p-6 h-full flex flex-col transition-all duration-300 relative ${
                isEnabled 
                  ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' 
                  : 'opacity-60 cursor-not-allowed bg-gray-50'
              }`}
            >
              {/* Badge de statut */}
              <div className="flex items-center justify-between mb-6">
                <ServiceIcon 
                  service={service.id} 
                  className={`w-12 h-12 transition-colors ${
                    isEnabled ? 'text-primary-500' : 'text-gray-400'
                  }`} 
                />
                
                {!isEnabled && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isInMaintenance 
                      ? 'bg-orange-100 text-orange-800 border border-orange-300' 
                      : 'bg-red-100 text-red-800 border border-red-300'
                  }`}>
                    {isInMaintenance ? '🔧 Maintenance' : '🚫 Indisponible'}
                  </span>
                )}
                
                {isEnabled && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                    ✓ Disponible
                  </span>
                )}
              </div>

              <h3 className={`text-2xl font-bold mb-3 ${
                isEnabled ? 'text-gray-900' : 'text-gray-600'
              }`}>
                {service.name}
              </h3>

              <p className={`mb-6 flex-grow leading-relaxed ${
                isEnabled ? 'text-gray-600' : 'text-gray-500'
              }`}>
                {service.description}
              </p>

              <div className="space-y-2">
                <h4 className={`font-semibold text-sm ${
                  isEnabled ? 'text-gray-900' : 'text-gray-600'
                }`}>
                  Caractéristiques:
                </h4>
                <ul className="space-y-1">
                  {service.features.map((feature, index) => (
                    <li key={index} className={`text-sm flex items-center ${
                      isEnabled ? 'text-gray-600' : 'text-gray-500'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                        isEnabled ? 'bg-primary-500' : 'bg-gray-400'
                      }`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {isEnabled ? (
                <button className="mt-6 btn-primary w-full py-3 group">
                  <span className="flex items-center justify-center">
                    Configurer ce service
                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              ) : (
                <div className="mt-6 w-full py-3 bg-gray-200 text-gray-600 rounded-lg text-center font-medium">
                  {isInMaintenance ? 'Service en maintenance' : 'Service non disponible'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Section Panneau DNS */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl p-8 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Icône et description */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <FaServer className="w-8 h-8" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-3">
                Panneau de Gestion DNS
              </h2>
              <p className="text-lg opacity-90 mb-4">
                Gérez vos zones DNS et enregistrements en toute simplicité. 
                Configuration avancée des domaines avec une interface intuitive.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                  <FaGlobe className="w-4 h-4" />
                  <span>Gestion de zones</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                  <FaCog className="w-4 h-4" />
                  <span>Enregistrements DNS</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                  <FaServer className="w-4 h-4" />
                  <span>Nameservers</span>
                </div>
              </div>
            </div>

            {/* Bouton d'action */}
            <div className="flex-shrink-0">
              <a
                href="/dns/auth"
                className="group bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all duration-300 flex items-center space-x-3 hover:scale-105"
              >
                <span>Accéder au Panneau</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="text-center text-sm mt-3 opacity-75">
                Authentification sécurisée par OTP
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section support avec design classique */}
      <div className="bg-gradient-to-r from-primary-500 to-secondary-400 rounded-xl p-8 text-white text-center">
        <h2 className="text-2xl font-bold mb-4">Besoin d'aide pour choisir ?</h2>
        <p className="text-lg opacity-90 mb-6">
          Notre équipe technique est disponible pour vous conseiller dans le choix 
          de la solution la plus adaptée à vos besoins.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="mailto:support@mapatacloud.com" 
            className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Contacter le support
          </a>
          <a 
            href="tel:+237123456789" 
            className="bg-white/20 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/30 transition-colors"
          >
            Appeler maintenant
          </a>
        </div>
      </div>
      </div>
    </>
  );
};

export default ServiceSelection;
